import { useState, useRef, useEffect } from 'react';
import type { SavedViewsAPI } from '../hooks/useSavedView';

/**
 * Dropdown for switching, saving, renaming, and deleting dashboard views.
 *
 * Renders as a Quiet Authority-styled inline group:
 *   [ active view name  ▾ ]  [ + Save as ]
 *
 * Clicking the active-view button opens a list with star-marked default,
 * rename + delete affordances per row, and an empty-state CTA when no
 * views exist yet.
 */
export function SavedViewsMenu<T>({
  api,
  label = 'View',
  className = '',
}: {
  api: SavedViewsAPI<T>;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setRenaming(null);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function handleSaveAs() {
    const name = window.prompt('Name this view');
    if (!name) return;
    api.saveAs(name.trim()).catch(() => { /* surfaced via UI later */ });
  }

  function commitRename(id: string) {
    const next = renameDraft.trim();
    if (next) api.rename(id, next).catch(() => {});
    setRenaming(null);
    setRenameDraft('');
  }

  const activeName = api.activeView?.name ?? '(no view)';

  return (
    <div ref={rootRef} className={`relative inline-flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="qa-input flex items-center gap-2 text-sm px-3 py-1.5"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${activeName}`}
      >
        <span className="text-ink-mute text-xs uppercase tracking-wide">{label}</span>
        <span className="text-ink font-medium">{activeName}</span>
        {api.activeView?.isDefault && <span className="text-rust" title="Default view" aria-hidden>★</span>}
        <span className="text-ink-mute ml-1" aria-hidden>▾</span>
      </button>
      <button
        type="button"
        onClick={handleSaveAs}
        className="btn-ghost text-xs"
        title="Save current filters as a new named view"
      >
        + Save as
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-30 min-w-[280px] qa-card qa-shadow"
          role="listbox"
        >
          {api.views.length === 0 ? (
            <div className="px-3 py-3 text-sm text-ink-mute">
              No saved views yet. Use <em>Save as</em> to capture your current filters.
            </div>
          ) : (
            <ul className="py-1 max-h-80 overflow-auto">
              {api.views.map(v => (
                <li key={v.id} className={`group flex items-center gap-2 px-3 py-1.5 text-sm ${api.activeView?.id === v.id ? 'bg-rust/5' : 'hover:bg-parchment'}`}>
                  {renaming === v.id ? (
                    <input
                      autoFocus
                      value={renameDraft}
                      onChange={e => setRenameDraft(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitRename(v.id);
                        if (e.key === 'Escape') { setRenaming(null); setRenameDraft(''); }
                      }}
                      onBlur={() => commitRename(v.id)}
                      className="qa-input flex-1 text-sm py-0.5"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => { api.switchTo(v.id); setOpen(false); }}
                      className="flex-1 text-left"
                      role="option"
                      aria-selected={api.activeView?.id === v.id}
                    >
                      {v.name}
                      {v.isDefault && <span className="ml-2 text-rust" title="Default" aria-label="default view">★</span>}
                    </button>
                  )}
                  <button
                    type="button"
                    title={v.isDefault ? 'Already default' : 'Set as default'}
                    onClick={() => api.setDefault(v.id).catch(() => {})}
                    className={`text-xs px-1 opacity-0 group-hover:opacity-100 ${v.isDefault ? 'text-rust' : 'text-ink-mute hover:text-rust'}`}
                  >
                    ★
                  </button>
                  <button
                    type="button"
                    title="Rename"
                    onClick={() => { setRenaming(v.id); setRenameDraft(v.name); }}
                    className="text-xs px-1 opacity-0 group-hover:opacity-100 text-ink-mute hover:text-ink"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    title="Delete view"
                    onClick={() => {
                      if (window.confirm(`Delete view "${v.name}"?`)) api.remove(v.id).catch(() => {});
                    }}
                    className="text-xs px-1 opacity-0 group-hover:opacity-100 text-ink-mute hover:text-rust-deep"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-hairline-paper px-3 py-2 text-xs text-ink-mute">
            Views sync across devices when you're signed in.
          </div>
        </div>
      )}
    </div>
  );
}
